import { Select } from 'components/atoms/Form/Select';
import type { SelectProps } from 'components/atoms/Form/Select/select.types';
import { SelectableCard } from 'components/molecules';
import { Helper, type HelperProps } from 'components/molecules/Helper';
import type { FieldProps } from 'components/pages/Expenses/Form/Fields/commonFieldProps.types';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useModal from 'hooks/useModal';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import { ExpenseVATRegime } from 'types/expenses.types';
import { getTracingEvents } from 'utils/amplitude';
import { FileMissingIcon } from 'utils/icons/figmaIcons';
import { DisallowedVATRegimesReason } from '../utils';

const markdownComponents: Components = {
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="underline break-word"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      {children}
    </a>
  ),
  p: ({ children }) => (
    <p className="whitespace-pre-wrap mb-2 last:mb-0">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-inside mb-2 last:mb-0">{children}</ul>
  ),
};

const ExemptHelper: FC<Omit<HelperProps, 'children'>> = (props) => {
  const { t } = useTranslation();
  const country = useGetCurrentCustomerData().data?.country;

  return (
    <Helper {...props}>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.exempt_item}.helper.q1.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.exempt_item}.helper.a1.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
    </Helper>
  );
};

const EuReverseChargeHelper: FC<Omit<HelperProps, 'children'>> = (props) => {
  const { t } = useTranslation();
  const country = useGetCurrentCustomerData().data?.country;

  return (
    <Helper {...props}>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.eu_reverse_charge}.helper.q1.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.eu_reverse_charge}.helper.a1.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
    </Helper>
  );
};

const ExtraEuReverseChargeHelper: FC<Omit<HelperProps, 'children'>> = (
  props,
) => {
  const { t } = useTranslation();
  const country = useGetCurrentCustomerData().data?.country;

  return (
    <Helper {...props}>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}.helper.q1.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}.helper.a1.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}.helper.q2.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}.helper.a2.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
    </Helper>
  );
};

const FranchiseeSupplierHelper: FC<Omit<HelperProps, 'children'>> = (props) => {
  const { t } = useTranslation();
  const country = useGetCurrentCustomerData().data?.country;

  return (
    <Helper {...props}>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.franchisee_supplier}.helper.q1.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.franchisee_supplier}.helper.a1.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
    </Helper>
  );
};

const LocalReverseChargeHelper: FC<Omit<HelperProps, 'children'>> = (props) => {
  const { t } = useTranslation();
  const country = useGetCurrentCustomerData().data?.country;

  return (
    <Helper {...props}>
      <Helper.Title>
        {t(
          `expense.vat_regime.${ExpenseVATRegime.local_reverse_charge}.helper.q1.${country}`,
        )}
      </Helper.Title>
      <Helper.Paragraph>
        <ReactMarkdown components={markdownComponents}>
          {t(
            `expense.vat_regime.${ExpenseVATRegime.local_reverse_charge}.helper.a1.${country}`,
          )}
        </ReactMarkdown>
      </Helper.Paragraph>
    </Helper>
  );
};

const helpers = {
  [ExpenseVATRegime.exempt_item]: ExemptHelper,
  [ExpenseVATRegime.eu_reverse_charge]: EuReverseChargeHelper,
  [ExpenseVATRegime.extra_eu_reverse_charge]: ExtraEuReverseChargeHelper,
  [ExpenseVATRegime.franchisee_supplier]: FranchiseeSupplierHelper,
  [ExpenseVATRegime.local_reverse_charge]: LocalReverseChargeHelper,
  [ExpenseVATRegime.standard]: () => null,
  [ExpenseVATRegime.foreign_vat]: () => null,
};

type CommonProps = {
  masterVATRegime: ExpenseVATRegime | null;
  hasManyItemsWithMasterVATRegime: boolean;
  allowedVATRegimes: ExpenseVATRegime[];
  suggestedValue?: ExpenseVATRegime;
  aiTooltip?: SelectProps['aiTooltip'];
  disallowedVATRegimes: Record<string, DisallowedVATRegimesReason | undefined>;
  hiddenVATRegimes: ExpenseVATRegime[];
};

type OptionsUIProps = CommonProps & {
  options: {
    label: string;
    description: string;
    value: ExpenseVATRegime;
    isDisabled?: boolean;
  }[];
  value?: ExpenseVATRegime;
  onChange: (value: ExpenseVATRegime) => void;
  onCancel: () => void;
};

const OptionsUI: FC<OptionsUIProps> = ({
  options,
  value: selected,
  onChange,
  // onCancel,
  // masterVATRegime,
  // hasManyItemsWithMasterVATRegime,
  allowedVATRegimes,
  suggestedValue,
  disallowedVATRegimes,
  hiddenVATRegimes,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="px-10 pt-8 pb-6 rounded-t-2xl text-center">
        <div className="rounded-full bg-expenses-100 h-12 w-12 relative mx-auto mb-4">
          <FileMissingIcon className="w-8 h-8 abs-center text-[#ED6CAE]" />
        </div>
        <h1 className="font-avenir text-[#5D4870] text-2xl font-bold">
          <p>{t('expense.vat_regime')}</p>
        </h1>
      </div>
      <div className="px-10 pb-10">
        <div className="flex flex-col gap-3">
          {options.map((option) => {
            const isHidden = hiddenVATRegimes.includes(option.value);
            if (isHidden) return null;
            const isDisabled = !allowedVATRegimes.includes(option.value);
            const withDisableReason = disallowedVATRegimes[option.value];
            const HelperButton = helpers[option.value];
            const isSelected = selected === option.value;

            const shouldShowSuggestedTag =
              !isDisabled && suggestedValue === option.value;

            return (
              <SelectableCard
                key={option.value}
                label={option.label}
                description={option.description}
                selected={isSelected}
                disabled={isDisabled}
                suggested={shouldShowSuggestedTag}
                tooltip={
                  isDisabled && withDisableReason
                    ? t(`expense.vat_regime.disabled.${withDisableReason}`)
                    : undefined
                }
                helper={
                  HelperButton ? (
                    <HelperButton
                      iconClassName="!w-6 !h-6"
                      triggerColor="gray"
                    />
                  ) : undefined
                }
                renderMarkdown={true}
                onClick={() => onChange(option.value)}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

const VATRegime: FC<FieldProps & CommonProps> = ({
  value,
  name,
  onChange,
  tracing,
  error,
  masterVATRegime,
  hasManyItemsWithMasterVATRegime,
  allowedVATRegimes,
  suggestedValue,
  disabled,
  aiTooltip,
  disallowedVATRegimes,
  hiddenVATRegimes,
}) => {
  const { t } = useTranslation();

  const country = useGetCurrentCustomerData().data?.country;

  const [modalContent, modalApi] = useModal({
    dialogPanalClassName: '!p-0',
    width: '2xl',
    name: 'expense vat regime',
  });

  const options = [
    {
      label: t(`expense.vat_regime.${ExpenseVATRegime.exempt_item}.temp`),
      description: t(
        `expense.vat_regime.${ExpenseVATRegime.exempt_item}.desc.${country}.temp`,
        { userCountry: t(`country.${country}`) },
      ),
      value: ExpenseVATRegime.exempt_item,
    },
    {
      label: t(`expense.vat_regime.${ExpenseVATRegime.eu_reverse_charge}`),
      description: t(
        `expense.vat_regime.${ExpenseVATRegime.eu_reverse_charge}.desc.${country}`,
      ),
      value: ExpenseVATRegime.eu_reverse_charge,
    },
    {
      label: t(
        `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}`,
      ),
      description: t(
        `expense.vat_regime.${ExpenseVATRegime.extra_eu_reverse_charge}.desc.temp`,
        { userCountry: t(`country.${country}`) },
      ),
      value: ExpenseVATRegime.extra_eu_reverse_charge,
    },
    {
      label: t(
        `expense.vat_regime.${ExpenseVATRegime.franchisee_supplier}.${country}`,
      ),
      description: t(
        `expense.vat_regime.${ExpenseVATRegime.franchisee_supplier}.desc.${country}`,
      ),
      value: ExpenseVATRegime.franchisee_supplier,
    },
    {
      label: t(
        `expense.vat_regime.${ExpenseVATRegime.local_reverse_charge}.${country}`,
      ),
      description: t(
        `expense.vat_regime.${ExpenseVATRegime.local_reverse_charge}.desc.${country}`,
      ),
      value: ExpenseVATRegime.local_reverse_charge,
    },
  ];

  const selected =
    options.find((option) => option.value === value)?.label || null;

  const tracingEvents = getTracingEvents(tracing, { onChange });

  return (
    <>
      {modalContent}
      <Select
        aiTooltip={aiTooltip}
        error={error}
        name={name}
        value={selected}
        isDisabled={disabled}
        openMenuOnClick
        isSearchable={false}
        noOptionsMessage={() => null}
        placeholder=""
        onMenuOpen={() => {
          tracingEvents.onMenuOpen?.();
          modalApi.open(() => (
            <OptionsUI
              value={value}
              options={options}
              disallowedVATRegimes={disallowedVATRegimes}
              hiddenVATRegimes={hiddenVATRegimes}
              masterVATRegime={masterVATRegime}
              hasManyItemsWithMasterVATRegime={hasManyItemsWithMasterVATRegime}
              allowedVATRegimes={allowedVATRegimes}
              suggestedValue={suggestedValue}
              onCancel={() => modalApi.close()}
              onChange={(value) => {
                tracingEvents.onChange?.(value);
                modalApi.close();
              }}
            />
          ));
        }}
      />
    </>
  );
};

export default VATRegime;
